import React, { useCallback, type Ref, type MouseEvent, useMemo } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { asyncInlineCreateStatusModal } from '@atlassian/jira-inline-create-status-modal/entrypoint.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { defaultModalProps } from '@atlassian/jira-status-config-common/src/constants.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { Trigger, useTriggerContext } from '@atlassian/entry-point-panel';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import {
	ISSUE_VIEW_SOURCE,
	type ConfigureSource,
} from '@atlassian/jira-status-config-common/src/types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormDialogSkeleton } from '@atlassian/jira-form-dialog-skeleton/src/ui/FormDialogSkeleton.tsx';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import { createStatusExperience } from '../../../experiences.tsx';
import { CreateOrEditStatusSkeleton } from '../../../common/ui/skeleton/CreateOrEditStatusSkeleton.tsx';
import { ConfigureStatusPanel } from './ConfigureStatusPanel.tsx';
import { messages } from './messages.tsx';

export type CreateStatusOptionProps = {
	projectId: string;
	projectKey: string;
	projectType: ProjectType;
	issueTypeId: string;
	statusNames?: string[];
	onClick: () => void;
	onClose: () => void;
	onStatusCreated?: () => Promise<void>;
	buttonRef: Ref<HTMLButtonElement>;
	source?: ConfigureSource;
	shouldTriggerModal?: boolean;
};

export const CreateStatusOption = ({
	projectId,
	projectKey,
	projectType,
	issueTypeId,
	statusNames = [],
	onClick,
	onClose,
	onStatusCreated,
	buttonRef,
	source,
	shouldTriggerModal = true,
}: CreateStatusOptionProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const trigger = useTriggerContext(ConfigureStatusPanel);

	const handleClick = useCallback(
		(_e: MouseEvent<HTMLElement>) => {
			onClick();
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'createStatusButton');
			// Because we are triggering the click manually for keyboard users
			// this handler becomes the source-of-truth handler for both keyboard users and mouse users
			// However, we need to stopPropagation from here to prevent double modal for mouse users
			// When mouse user clicks the button, if we don't stop propagation here, handleChange
			// will be called which will trigger click manually again and cause double modal rendering
			_e.stopPropagation();
		},
		[onClick, createAnalyticsEvent],
	);

	const onModalClose = useCallback(() => {
		onClose();
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'closed',
				actionSubject: 'inlineCreateStatusModal',
			}),
		);
	}, [createAnalyticsEvent, onClose]);

	const onMouseDown = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'createStatusButton',
		);
	}, [createAnalyticsEvent]);

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			projectKey,
		}),
		[cloudId, projectKey],
	);

	const entryPointProps = useMemo(
		() => ({
			statusNames,
			projectId,
			projectKey,
			projectType,
			onClose: onModalClose,
			isNewControllerEnabled: true,
			workflowAssociatedIssueTypeId: issueTypeId,
			createStatusExperience,
			source: source || ISSUE_VIEW_SOURCE,
			onStatusCreated,
			isWrappedInModal: shouldTriggerModal,
		}),
		[
			statusNames,
			projectId,
			projectKey,
			projectType,
			onModalClose,
			issueTypeId,
			source,
			onStatusCreated,
			shouldTriggerModal,
		],
	);

	const getClickElement = useCallback(
		(ref: React.RefObject<HTMLElement>) => (
			// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
			<Box
				role="button"
				xcss={optionStyles}
				onClick={handleClick}
				data-show-on-hover
				ref={ref}
				onMouseDown={(event: MouseEvent<HTMLElement>) => {
					if (event.button === 0) {
						// Left mouse button was clicked
						onMouseDown();
					}
				}}
			>
				{formatMessage(messages.createStatus)}
			</Box>
		),
		[formatMessage, handleClick, onMouseDown],
	);

	const modalTrigger = fg('inline_config_migrate_deprecated_modal_entrypoints') ? (
		<ModalTrigger
			entryPoint={asyncInlineCreateStatusModal}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			modalProps={defaultModalProps}
			forwardedRef={buttonRef}
			Fallback={() => <FormDialogSkeleton numFormFields={2} />}
		>
			{({ ref }) => getClickElement(ref)}
		</ModalTrigger>
	) : (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncInlineCreateStatusModal}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			interactionName="create-status-modal"
			modalProps={defaultModalProps}
			useInternalModal
			forwardedRef={buttonRef}
			Fallback={
				fg('jgfeng_new_work_type_fallback_loading_state')
					? () => <FormDialogSkeleton numFormFields={2} />
					: undefined
			}
		>
			{({ ref }) => getClickElement(ref)}
		</ModalEntryPointPressableTrigger>
	);

	return shouldTriggerModal ? (
		modalTrigger
	) : (
		<Trigger
			trigger={trigger}
			entryPoint={asyncInlineCreateStatusModal}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			fallback={
				fg('jgfeng_form_dialog_skeleton') ? (
					<FormDialogSkeleton numFormFields={2} />
				) : (
					<CreateOrEditStatusSkeleton numFormFields={2} />
				)
			}
		>
			{({ ref }) => getClickElement(ref)}
		</Trigger>
	);
};

const optionStyles = xcss({
	height: '100%',
	width: '100%',
	backgroundColor: 'color.background.neutral.subtle',
	alignItems: 'center',
	textAlign: 'left',
	fontWeight: 'font.weight.medium',
});
